/**
 * Product detail (sections/main-product-detail.liquid)
 * <product-detail>
 *  - Variant pills: finds the variant, updates the URL, then re-renders price, pills
 *    (sold out states) and buttons with the Section Rendering API (correct currency/markets)
 *  - Gallery: mobile "1 / 11" counter, jumps to the variant's image
 *  - Info tabs: click + arrow keys (WAI-ARIA tabs)
 *  - Size guide: <dialog> bottom sheet / corner panel with IN / CM toggle
 * Native browser APIs only. Add to cart uses the theme's <add-to-cart> element (cart drawer).
 */
(() => {
  if (customElements.get('product-detail')) return;

  const MOBILE_QUERY = '(max-width: 950px)';

  class ProductDetail extends HTMLElement {
    #controller = new AbortController();
    /** @type {AbortController | null} */
    #fetchController = null;
    /** @type {Array<{id: number, options: string[], available: boolean, featured_media?: {id: number}}>} */
    #variants = [];
    /** @type {IntersectionObserver | null} */
    #counterObserver = null;
    /** @type {{position: number, handles: Record<string, string>} | null | undefined} */
    #groups = undefined;

    connectedCallback() {
      const { signal } = this.#controller;
      try {
        this.#variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
      } catch {
        this.#variants = [];
      }

      this.addEventListener('change', this.#onOptionChange.bind(this), { signal });
      this.#setupCounter();
      this.#setupTabs();
      this.#setupSizeGuide();
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
      this.#fetchController?.abort();
      this.#counterObserver?.disconnect();
    }

    /* ---------- Variants ---------- */

    /** @param {Event} event */
    #onOptionChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.dataset.optionPosition) return;

      const selected = this.#selectedOptions();
      const variant = this.#variants.find((item) => item.options.every((value, index) => value === selected[index]));
      this.#renderVariant(variant, input.id);
    }

    /** Current option values in position order (radios + hidden inputs for hidden options). */
    #selectedOptions() {
      const values = [];
      for (const field of this.querySelectorAll('[data-pd-options] [data-option-position]')) {
        if (field instanceof HTMLInputElement && (field.type === 'hidden' || field.checked)) {
          values[Number(field.dataset.optionPosition) - 1] = field.value;
        }
      }
      return values;
    }

    /**
     * @param {{id: number, available: boolean, featured_media?: {id: number}} | undefined} variant
     * @param {string} focusId - id of the input to keep focused after re-render
     */
    async #renderVariant(variant, focusId) {
      const addButton = this.querySelector('.product-detail__button--add');

      if (!variant) {
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = addButton.dataset.unavailableLabel || addButton.textContent;
        }
        return;
      }

      for (const input of this.querySelectorAll('[data-pd-variant-input]')) input.value = String(variant.id);
      this.querySelector('add-to-cart')?.setAttribute('data-variant-id', String(variant.id));
      this.querySelector('wishlist-toggle')?.setAttribute('data-variant-id', String(variant.id));

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      }

      const filtered = this.#filterMedia(variant);
      const featured = variant.featured_media?.id;
      const target = featured && this.querySelector(`[data-gallery-item][data-media-id="${featured}"]:not([hidden])`)
        ? featured
        : filtered;
      if (target) this.#showMedia(target);

      this.#fetchController?.abort();
      this.#fetchController = new AbortController();
      try {
        const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
        url.searchParams.set('variant', String(variant.id));
        url.searchParams.set('section_id', this.dataset.sectionId || '');
        const response = await fetch(url, { signal: this.#fetchController.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        for (const selector of ['[data-pd-price]', '[data-pd-options]', '[data-pd-buttons]']) {
          const next = html.querySelector(selector);
          const current = this.querySelector(selector);
          if (next && current) current.innerHTML = next.innerHTML;
        }

        if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
        window.Shopify?.PaymentButton?.init?.();
        this.dispatchEvent(new CustomEvent('product-detail:variant-change', { bubbles: true, detail: { variant } }));
      } catch (error) {
        if (error.name !== 'AbortError') console.error('[product-detail] variant update failed', error);
      }
    }

    /**
     * "Show only the selected colour's images": keeps the images of the variant's colour (plus images
     * shared by all colours) and hides the rest. A colour without its own images shows everything.
     * @param {{options?: string[]}} variant
     * @returns {number | null} id of the first visible image when the selection changed, else null
     */
    #filterMedia(variant) {
      const config = this.#mediaGroups();
      const items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      if (!config || !items.length || !variant?.options) return null;

      const value = variant.options[config.position - 1];
      const group = config.handles[value] || '';
      const hasOwn = group && items.some((item) => item.dataset.mediaGroup === group);
      let firstVisible = null;
      for (const item of items) {
        const show = !hasOwn || !item.dataset.mediaGroup || item.dataset.mediaGroup === group;
        item.hidden = !show;
        if (show && firstVisible === null) firstVisible = Number(item.dataset.mediaId) || null;
      }
      this.#updateCounter();
      return firstVisible;
    }

    /** @returns {{position: number, handles: Record<string, string>} | null} */
    #mediaGroups() {
      if (this.#groups !== undefined) return this.#groups;
      try {
        this.#groups = JSON.parse(this.querySelector('[data-media-groups]')?.textContent || 'null');
      } catch {
        this.#groups = null;
      }
      return this.#groups;
    }

    /** @param {number} mediaId */
    #showMedia(mediaId) {
      const item = this.querySelector(`[data-gallery-item][data-media-id="${mediaId}"]`);
      const list = this.querySelector('[data-gallery-list]');
      if (!item || !list) return;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      if (window.matchMedia(MOBILE_QUERY).matches) {
        list.scrollTo({ left: item.offsetLeft - list.offsetLeft, behavior });
        return;
      }
      const top = item.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior });
    }

    /* ---------- Gallery counter (mobile) ---------- */

    #setupCounter() {
      const current = this.querySelector('[data-gallery-current]');
      const list = this.querySelector('[data-gallery-list]');
      if (!current || !list || !('IntersectionObserver' in window)) return;

      const items = Array.from(list.querySelectorAll('[data-gallery-item]'));
      this.#counterObserver = new IntersectionObserver((entries) => {
        const visible = items.filter((item) => !item.hidden);
        for (const entry of entries) {
          if (entry.isIntersecting && !entry.target.hidden) current.textContent = String(visible.indexOf(entry.target) + 1);
        }
      }, { root: list, threshold: 0.6 });

      for (const item of items) this.#counterObserver.observe(item);
      this.#updateCounter();
    }

    /** "1 / 6": total = visible images (colour filter), back to 1 after a change. */
    #updateCounter() {
      const total = this.querySelector('[data-gallery-total]');
      if (!total) return;
      const count = this.querySelectorAll('[data-gallery-item]:not([hidden])').length;
      total.textContent = String(count);
      const current = this.querySelector('[data-gallery-current]');
      if (current && Number(current.textContent) > count) current.textContent = '1';
    }

    /* ---------- Tabs ---------- */

    #setupTabs() {
      const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      const { signal } = this.#controller;

      const select = (tab, focus = false) => {
        for (const item of tabs) {
          const active = item === tab;
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(item.getAttribute('aria-controls') || '');
          if (panel) panel.hidden = !active;
        }
        if (focus) tab.focus();
      };

      for (const [index, tab] of tabs.entries()) {
        tab.addEventListener('click', () => select(tab), { signal });
        tab.addEventListener('keydown', (event) => {
          const isRtl = getComputedStyle(this).direction === 'rtl';
          let next = null;
          if (event.key === 'ArrowRight') next = tabs[(index + (isRtl ? -1 : 1) + tabs.length) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index + (isRtl ? 1 : -1) + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          event.preventDefault();
          select(next, true);
        }, { signal });
      }
    }

    /* ---------- Size guide ---------- */

    /**
     * Glasses guide "Talk to an expert": ONLINE during support hours (store time zone, from the
     * page's UTC offset), OFFLINE otherwise. Hours: data-open / data-close (0-24).
     * @param {Element} root
     */
    #setupSupportStatus(root) {
      const box = root.querySelector('[data-support-hours]');
      if (!(box instanceof HTMLElement)) return;
      const match = /^([+-])(\d{2})(\d{2})$/.exec(box.dataset.offset || '');
      const offsetMinutes = match ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3])) : -new Date().getTimezoneOffset();
      const now = new Date(Date.now() + offsetMinutes * 60000);
      const hour = now.getUTCHours() + now.getUTCMinutes() / 60;
      const open = Number(box.dataset.open);
      const close = Number(box.dataset.close);
      // Also handles hours that run past midnight (e.g. 20 -> 2)
      const online = open < close ? hour >= open && hour < close : hour >= open || hour < close;

      for (const status of box.querySelectorAll('[data-support-status]')) {
        status.textContent = online ? box.dataset.labelOnline || 'ONLINE' : box.dataset.labelOffline || 'OFFLINE';
        status.classList.toggle('is-online', online);
      }
    }

    #setupSizeGuide() {
      const dialog = this.querySelector('[data-size-guide]');
      if (!(dialog instanceof HTMLDialogElement)) return;
      const { signal } = this.#controller;

      const close = () => {
        if (!dialog.open) return;
        dialog.classList.remove('is-open');
        setTimeout(() => dialog.close(), 250);
      };

      // Delegated: the size guide button lives inside a part that is re-rendered on variant change.
      this.addEventListener('click', (event) => {
        const opener = event.target instanceof Element ? event.target.closest('[data-size-guide-open]') : null;
        if (!opener) return;
        dialog.showModal();
        requestAnimationFrame(() => dialog.classList.add('is-open'));
      }, { signal });

      dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', close, { signal });
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
      }, { signal });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      }, { signal });

      this.#setupSupportStatus(dialog);

      const toggle = dialog.querySelector('[data-unit-toggle]');
      if (!toggle) return;
      for (const button of toggle.querySelectorAll('[data-unit]')) {
        button.addEventListener('click', () => {
          const unit = button.getAttribute('data-unit');
          toggle.classList.toggle('is-cm', unit === 'cm');
          for (const other of toggle.querySelectorAll('[data-unit]')) {
            other.setAttribute('aria-pressed', String(other === button));
          }
          for (const table of dialog.querySelectorAll('[data-size-unit]')) {
            table.hidden = table.getAttribute('data-size-unit') !== unit;
          }
        }, { signal });
      }
    }
  }

  customElements.define('product-detail', ProductDetail);
})();
